import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Stack, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { renderAsync } from 'docx-preview';
import './docx-preview.css';

const WordViewer = ({ arrayBuffer }) => {
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentMatch, setCurrentMatch] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // Convert DOCX to HTML using docx-preview when arrayBuffer changes
  useEffect(() => {
    if (arrayBuffer && containerRef.current) {
      console.log('WordViewer: Rendering DOCX document');
      setIsLoaded(false);

      // Clear container before rendering
      containerRef.current.innerHTML = '';

      // Use docx-preview for better formatting and page breaks
      renderAsync(arrayBuffer, containerRef.current, containerRef.current, {
        className: 'docx-preview-container',
        inWrapper: true,
        useBase64URL: true,
        breakPages: true, // Enable page breaks
        pageMargins: { top: 20, right: 20, bottom: 20, left: 20 },
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        ignoreLastRenderedPageBreak: false,
        ignoreFonts: false,
        renderEndnotes: true,
      })
        .then(() => {
          console.log('DOCX rendered successfully');
          setIsLoaded(true);
          
          // Add page numbers to each page
          addPageNumbers();
        })
        .catch((error) => {
          console.error('Error rendering DOCX:', error);
        });
    }
  }, [arrayBuffer]);

  // Add page numbers to each page
  const addPageNumbers = () => {
    if (!containerRef.current) return;

    const pages = containerRef.current.querySelectorAll('.docx-viewer-page');
    
    pages.forEach((page, index) => {
      const pageNumber = document.createElement('div');
      pageNumber.className = 'page-number';
      pageNumber.textContent = `Page ${index + 1} of ${pages.length}`;
      pageNumber.style.textAlign = 'center';
      pageNumber.style.marginTop = '10px';
      pageNumber.style.fontSize = '12px';
      pageNumber.style.color = '#666';
      pageNumber.style.position = 'absolute';
      pageNumber.style.bottom = '10px';
      pageNumber.style.width = '100%';
      
      // Make sure the page is positioned relatively
      page.style.position = 'relative';
      
      page.appendChild(pageNumber);
    });
  };

  // Handle zoom functionality
  const handleZoomIn = () => setZoom((prev) => prev + 0.1);
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));
  
  // Handle print functionality
  const handlePrint = () => {
    const printWindow = window.open('', 'PrintWindow');
    
    // Add all necessary styles
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Document</title>
          <style>
            .docx-preview-container {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
            }
            .docx-viewer-page {
              page-break-after: always;
              position: relative;
              margin-bottom: 20px;
              border-bottom: 1px dashed #ccc;
              padding-bottom: 20px;
            }
            .docx-viewer-page:last-child {
              page-break-after: auto;
            }
            .page-number {
              text-align: center;
              margin-top: 10px;
              font-size: 12px;
              color: #666;
            }
            @media print {
              .page-search-highlight {
                background-color: transparent !important;
                color: inherit !important;
              }
            }
          </style>
        </head>
        <body>
    `);
    printWindow.document.write(containerRef.current.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Handle search functionality
  const handleSearch = () => {
    if (!searchTerm.trim() || !containerRef.current || !isLoaded) return;

    // Remove existing highlights
    const existingHighlights = containerRef.current.querySelectorAll('.page-search-highlight');
    existingHighlights.forEach(el => {
      const parent = el.parentNode;
      parent.replaceChild(document.createTextNode(el.textContent), el);
      parent.normalize();
    });

    // Perform new search
    const textNodes = getTextNodes(containerRef.current);
    let matchCount = 0;
    const matchPositions = [];

    textNodes.forEach(node => {
      const text = node.nodeValue;
      const searchTermLower = searchTerm.toLowerCase();
      const textLower = text.toLowerCase();
      
      let startIndex = 0;
      let index;
      
      while ((index = textLower.indexOf(searchTermLower, startIndex)) > -1) {
        matchCount++;
        matchPositions.push({ node, index, length: searchTerm.length });
        startIndex = index + searchTermLower.length;
      }
    });

    // Highlight matches
    matchPositions.forEach((match, idx) => {
      const { node, index, length } = match;
      
      const range = document.createRange();
      range.setStart(node, index);
      range.setEnd(node, index + length);
      
      const span = document.createElement('span');
      span.className = 'page-search-highlight';
      span.style.backgroundColor = idx === currentMatch ? '#ffcc00' : '#ffff00';
      span.style.color = '#000';
      
      range.surroundContents(span);
    });

    setSearchResults(matchPositions);
    setCurrentMatch(matchPositions.length > 0 ? 0 : -1);
    
    // Scroll to first match if available
    if (matchPositions.length > 0) {
      scrollToMatch(0);
    }
  };

  // Helper function to get all text nodes in an element
  const getTextNodes = (node) => {
    const textNodes = [];
    
    const traverse = (node) => {
      // Skip script tags and style tags
      if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
      
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim().length > 0) {
        textNodes.push(node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        for (let i = 0; i < node.childNodes.length; i++) {
          traverse(node.childNodes[i]);
        }
      }
    };
    
    traverse(node);
    return textNodes;
  };

  // Navigate between search results
  const handleNextMatch = () => {
    if (searchResults.length === 0) return;
    
    // Remove current highlight
    const highlights = containerRef.current.querySelectorAll('.page-search-highlight');
    highlights.forEach((highlight, idx) => {
      highlight.style.backgroundColor = idx === currentMatch ? '#ffff00' : highlight.style.backgroundColor;
    });
    
    // Move to next match
    const nextMatch = (currentMatch + 1) % searchResults.length;
    setCurrentMatch(nextMatch);
    
    // Highlight current match
    highlights[nextMatch].style.backgroundColor = '#ffcc00';
    
    // Scroll to match
    scrollToMatch(nextMatch);
  };

  const handlePrevMatch = () => {
    if (searchResults.length === 0) return;
    
    // Remove current highlight
    const highlights = containerRef.current.querySelectorAll('.page-search-highlight');
    highlights.forEach((highlight, idx) => {
      highlight.style.backgroundColor = idx === currentMatch ? '#ffff00' : highlight.style.backgroundColor;
    });
    
    // Move to previous match
    const prevMatch = (currentMatch - 1 + searchResults.length) % searchResults.length;
    setCurrentMatch(prevMatch);
    
    // Highlight current match
    highlights[prevMatch].style.backgroundColor = '#ffcc00';
    
    // Scroll to match
    scrollToMatch(prevMatch);
  };

  // Scroll to specific match
  const scrollToMatch = (matchIndex) => {
    const highlights = containerRef.current.querySelectorAll('.page-search-highlight');
    if (highlights.length > matchIndex) {
      highlights[matchIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Button variant="contained" onClick={handleZoomOut}>Zoom Out</Button>
        <Button variant="contained" onClick={handleZoomIn}>Zoom In</Button>
        <Button variant="contained" onClick={handlePrint}>Print</Button>
        
        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ width: '200px' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} edge="end">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        {searchResults.length > 0 && (
          <>
            <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {currentMatch + 1} of {searchResults.length}
            </Box>
            <IconButton onClick={handlePrevMatch} disabled={searchResults.length === 0}>
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton onClick={handleNextMatch} disabled={searchResults.length === 0}>
              <NavigateNextIcon />
            </IconButton>
          </>
        )}
      </Stack>

      <Box
        ref={containerRef}
        sx={{
          border: '1px solid #ccc',
          height: '500px',
          overflow: 'auto',
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          '& .docx-viewer-page': {
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            marginBottom: '20px',
            padding: '40px',
            position: 'relative',
            pageBreakAfter: 'always',
            borderBottom: '1px dashed #ccc'
          },
          '& .docx-viewer-page:last-child': {
            pageBreakAfter: 'auto'
          }
        }}
      />
    </Box>
  );
};

export default WordViewer;